import React from 'react';

export default function DashboardGuideComponent({ showGuide, setShowGuide }) {
  const guideContent = [
    {
      icon: (
        <svg className="w-6 h-6 text-[#036AA9]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      title: "DevKit Overview",
      description: "Monitor all your ProteusCore development kits in real-time. See which devices are online, their status, and recent activity."
    },
    {
      icon: (
        <svg className="w-6 h-6 text-[#036AA9]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      title: "Device Management",
      description: "Keep your ProteusCore DevKits up-to-date and access them remotely using our built-in SSH and VNC clients—control your devices from anywhere in the world."
    },
    {
      icon: (
        <svg className="w-6 h-6 text-[#036AA9]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a5 5 0 00-10 0v2M5 13h14l-1 9H6l-1-9z" />
        </svg>
      ),
      title: "Batch Update Panel",
      description: "Update multiple devices to a specific version at once. Only online devices will appear in the panel. You can select one or multiple devices (e.g., update all to ProteusCore v1.3.1 (433)). Each device's update progress is shown step-by-step in the panel. Devices update one at a time, so allow time for all updates to finish."
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-800 via-gray-900 to-black border-b border-gray-700 shadow-md text-white p-6 relative">
          <button
            onClick={() => setShowGuide(false)}
            className="absolute top-4 right-4 text-white hover:bg-white/20 rounded-full p-2 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="flex items-center gap-3">
            <svg className="w-8 h-8 text-[#EBEBEB]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            <h2 className="text-2xl font-bold">ProteusCore Dashboard Guide</h2>
          </div>
          <p className="mt-2 text-[#EBEBEB]">Everything you need to know about your ProteusCore DevKit Dashboard</p>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-[#0E103D] mb-2">Welcome to Your ProteusCore DevKit Dashboard!</h3>
            <p className="text-[#57585B]">
              This comprehensive dashboard helps you monitor and manage all your development kits from one central location. 
            </p>
          </div>

          {/* Feature Cards */}
          <div className="grid gap-4 md:grid-cols-2">
            {guideContent.map((item, index) => (
              <div
                key={index}
                className="border border-[#EBEBEB] rounded-lg p-4 hover:shadow-md dark:hover:border-[#405170] transition-shadow"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">{item.icon}</div>
                  <div>
                    <h4 className="font-semibold text-[#0E103D] mb-1">{item.title}</h4>
                    <p className="text-sm text-[#57585B]">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Tips */}
          <div className="mt-6 rounded-lg p-4 bg-[#036AA9]/10">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-5 h-5 text-[#036AA9]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h4 className="font-semibold text-[#0E103D]">Quick Tips</h4>
            </div>
            <ul className="space-y-1 text-sm text-[#57585B]">
              <li>• Use the view toggle to switch between grid and list views</li>
              <li>• Green indicators show online devices, red shows offline</li>
              <li>• The update actions allow you to update each of the system components individually</li>
              <li>• Missing a device? Reach out to Tocaro Blue support and we will promptly add it to your account</li>
              <li>• Both the Configuration App and ProteusCore have three release channels: Stable, Nightly and Experimental — We highly recommend staying on the Stable channel unless you are working directly with a Tocaro Blue Engineer</li>
            </ul>
          </div>

          {/* Getting Started */}
          <div className="mt-6 rounded-lg p-4 bg-[#036AA9]/10">
            <h4 className="font-semibold text-[#0E103D] mb-2">Getting Started</h4>
            <ol className="space-y-2 text-sm text-[#57585B] list-decimal list-inside">
              <li>Connect your DevKit to your network</li>
              <li>That's it! As soon as your Devkit has access to the internet it will come online in the dashboard</li>
            </ol>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-[#EBEBEB] dark:bg-[#1d2839] px-6 py-4 border-t border-[#EBEBEB]">
          <div className="flex justify-between items-center">
            <p className="text-sm text-[#57585B]">
              Need more help? Check our{' '}
              <a 
                href="http://docs.tocaroblue.com" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#036AA9] hover:text-[#005f95] underline font-medium"
              >
                documentation
              </a>
              {' '}or{' '}
              <a 
                href="mailto:tech@tocaroblue.com" 
                className="text-[#036AA9] hover:text-[#005f95] underline font-medium"
              >
                contact support
              </a>
            </p>
            <button
              onClick={() => setShowGuide(false)}
              className="px-4 py-2 rounded-md transition-all duration-300 ease-in-out
                bg-gradient-to-r from-slate-700 via-slate-800 to-slate-900 text-white
                hover:from-slate-600 hover:via-slate-700 hover:to-slate-800
                hover:shadow-lg hover:shadow-slate-700/40 hover:scale-105"
            >
              Got it!
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}